<template>
  <EasyFormField
    v-if="!loading"
    :name="props.name"
    :label="props.label"
    :required="props.isRequired"
  >
    <EasyTimePickerDropdown
      v-model:hours-value="hoursInputValue"
      v-model:minutes-value="inputValues.minutes"
      v-model:meridiem-value="inputValues.meridiem"
      :show-dropdown-value="showDropdown"
      :use-meridiem="useMeridiem"
      :max-hours="maxHours"
      @update:time-value="emitTimeUpdateModelValue"
      @change="emitChange"
    >
      <template #default="{ props: vMenuProps, menuEl }">
        <EasyTimePickerInput
          v-bind="vMenuProps"
          v-model:hours-value="hoursInputValue"
          v-model:minutes-value="inputValues.minutes"
          v-model:meridiem-value="inputValues.meridiem"
          :menu-el="menuEl"
          :name="name"
          :use-meridiem="useMeridiem"
          :max-hours="maxHours"
          @update:time-value="emitTimeUpdateModelValue"
          @blur="onTimePickerBlur"
          @focus="showDropdown = true"
          @change="emitChange"
        />
      </template>
    </EasyTimePickerDropdown>
  </EasyFormField>
</template>

<script setup lang="ts">
import EasyFormField from "../EasyFormField.vue";
import EasyTimePickerDropdown from "./EasyTimePickerDropdown.vue";
import EasyTimePickerInput from "./EasyTimePickerInput.vue";
import easyTimePickerUtils from "../../utils/easyTimePicker";
import timeEntryUtils from "@/src/shared/utils/timeEntry";
import { MAX_HOURS_TIME_INPUT_12H, MAX_HOURS_TIME_INPUT_24H } from "@/src/shared/constants/timeEntry";
import { TimeInputs, TimeParts } from "../../types/easyTimePicker";
import { computed, onMounted, reactive, ref, watch } from "vue";

interface Props {
  name: string;
  modelValue: string;
  label?: string;
  isRequired?: boolean;
  disabled?: boolean;
  timeFormat?: string;
  loading?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  label: "",
  isRequired: false,
  disabled: false,
  timeFormat: undefined,
  loading: false,
});

const useMeridiem = computed(() => timeEntryUtils.useMeridiem(window.EasySettings.getGlobalTimeFormat.value));
const maxHours = computed(() => (useMeridiem.value ? MAX_HOURS_TIME_INPUT_12H : MAX_HOURS_TIME_INPUT_24H));

interface Emits {
  (e: "update:modelValue", time: string): void;
  (e: "change", time: string): void;
  (e: "blur", time: string): void;
}
const emit = defineEmits<Emits>();

const showDropdown = ref<boolean>(false);

const inputValues = reactive<TimeInputs>({
  [TimeParts.Hours]: "",
  [TimeParts.Minutes]: "",
  [TimeParts.Meridiem]: "",
});

onMounted(() => setInputValues());
watch(
  () => props.modelValue,
  () => setInputValues()
);
watch(
  () => props.loading,
  isLoading => {
    if (!isLoading) setInputValues();
  }
);

const setInputValues = () => {
  const { hours, minutes, meridiem } = timeEntryUtils.extractTimePartsFromValue(props.modelValue, useMeridiem.value);
  inputValues[TimeParts.Hours] = hours;
  inputValues[TimeParts.Minutes] = minutes;
  inputValues[TimeParts.Meridiem] = meridiem;
};

const onTimePickerBlur = () => {
  showDropdown.value = false;
  const timeHHmm = easyTimePickerUtils.formatTimeToHHmm(inputValues, useMeridiem.value);
  emit("blur", timeHHmm);
};

const emitTimeUpdateModelValue = () => {
  showDropdown.value = false;
  const timeHHmm = easyTimePickerUtils.formatTimeToHHmm(inputValues, useMeridiem.value);
  emit("update:modelValue", timeHHmm);
};

const emitChange = () => {
  const timeHHmm = easyTimePickerUtils.formatTimeToHHmm(inputValues, useMeridiem.value);
  emit("change", timeHHmm);
};

const hoursInputValue = computed({
  get: () => {
    if (useMeridiem.value && inputValues[TimeParts.Hours] === "00") {
      return "12";
    }
    return inputValues[TimeParts.Hours];
  },
  set: (hours: string) => {
    if (useMeridiem.value && hours === "12") {
      inputValues[TimeParts.Hours] = "00";
      return;
    }
    inputValues[TimeParts.Hours] = hours;
  },
});
</script>
